import React, { useEffect, useMemo, useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, ClipboardCheck, ArrowRight, FlaskConical, Beaker, Search, Target, X, Play } from 'lucide-react';
import { getEnabledModes } from '../modes';
import { LabStation } from './LabStation';
import { MissionBriefing } from './MissionBriefing';
import { FamilyPicker, type FamilyPickerMode } from './FamilyPicker';
import { ChallengeAcceptModal } from './ChallengeAcceptModal';
import { FluencyIntro } from './FluencyIntro';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { PageHero, HeroStatPill } from '../components/shared/PageHero';
import { PageContainer } from '../components/shared/PageContainer';
import { itemVariants } from '../components/shared/animations';
import { WorldZone } from '../components/shared/WorldZone';
import { useDjaySelection, getDjayLabWelcome, getDjayModeIntro, DjayAvatar } from '../mascot';
import { useAppStore } from '../app/hooks/useAppStore';
import { getFamilyMasteryKey } from '../mastery';
import { dataLayer } from '../data/dataLayer';
import { isFluencyColdStart as checkFluencyColdStart } from '../modes';
import type { AssessmentHistoryEntry } from '../assessment';
import { getAssessmentHistory } from '../assessment/utils/assessmentStorage';
// ─── Constants ────────────────────────────────────────────────
// ─── Types ────────────────────────────────────────────────────
interface StartModeConfig {
  targetFamilyId?: string;
  targetModules?: string[];
}
interface LearningLabPageProps {
  onStartAssessment?: () => void;
  onStartMode?: (modeId: string, config?: StartModeConfig) => void;
}
// ─── Component ────────────────────────────────────────────────
/**
 * LearningLabPage — Main page for the practice lab ("Oefenlab").
 *
 * Orchestrates mode selection, family picking, and challenge acceptance.
 * Shows Djay's mission briefing, training mode stations, and extra options.
 * Part of the learningLab domain (DDD co-locatie s57).
 */
export function LearningLabPage({
  onStartAssessment,
  onStartMode
}: LearningLabPageProps) {
  const enabledModes = getEnabledModes();
  const {
    selectedVariant
  } = useDjaySelection();
  const {
    state,
    selectedGroup
  } = useAppStore();
  const labWelcome = useMemo(() => getDjayLabWelcome(), []);
  const [assessmentHistory, setAssessmentHistory] = useState<AssessmentHistoryEntry[]>([]);
  useEffect(() => {
    getAssessmentHistory(selectedGroup).then(setAssessmentHistory);
  }, [selectedGroup]);
  // ── Welcome banner state (B5) ──
  const [showWelcome, setShowWelcome] = useState(false);
  useEffect(() => {
    dataLayer.getOefenlabWelcomeDismissed().then(dismissed => {
      if (!dismissed) setShowWelcome(true);
    });
  }, []);
  const dismissWelcome = () => {
    setShowWelcome(false);
    dataLayer.setOefenlabWelcomeDismissed(true);
  };
  // ── FamilyPicker state ──
  const [familyPickerOpen, setFamilyPickerOpen] = useState(false);
  const [familyPickerMode, setFamilyPickerMode] = useState<FamilyPickerMode>('sprint');
  const [pendingModeId, setPendingModeId] = useState<string | null>(null);
  // ── ChallengeAcceptModal state ──
  const [challengeModalOpen, setChallengeModalOpen] = useState(false);
  const [pendingChallengeFamilyId, setPendingChallengeFamilyId] = useState<string | null>(null);
  // ── FluencyIntro state ──
  const [fluencyIntroOpen, setFluencyIntroOpen] = useState(false);
  const [pendingFluencyConfig, setPendingFluencyConfig] = useState<{
    familyId?: string;
  } | null>(null);
  // ── Handlers ──
  const handleModeClick = (modeId: string, familyId?: string) => {
    if (modeId === 'fluency') {
      dataLayer.getFluencyIntroSeen().then(seen => {
        if (!seen) {
          setPendingFluencyConfig({
            familyId
          });
          setFluencyIntroOpen(true);
        } else {
          if (familyId && onStartMode) {
            onStartMode('fluency', {
              targetFamilyId: familyId
            });
          } else {
            onStartMode?.('fluency');
          }
        }
      });
      return;
    }
    if (familyId && onStartMode) {
      if (modeId === 'challenge') {
        setPendingChallengeFamilyId(familyId);
        setChallengeModalOpen(true);
        return;
      }
      onStartMode(modeId === 'free' ? 'sprint' : modeId, {
        targetFamilyId: familyId
      });
      return;
    }
    if (modeId === 'sprint' || modeId === 'challenge' || modeId === 'free') {
      setPendingModeId(modeId);
      setFamilyPickerMode(modeId as FamilyPickerMode);
      setFamilyPickerOpen(true);
    } else {
      onStartMode?.(modeId);
    }
  };
  const handleFamilySelect = (familyId: string) => {
    setFamilyPickerOpen(false);
    if (pendingModeId === 'challenge') {
      setPendingChallengeFamilyId(familyId);
      setChallengeModalOpen(true);
      setPendingModeId(null);
      return;
    }
    if (onStartMode && pendingModeId) {
      onStartMode(pendingModeId, {
        targetFamilyId: familyId
      });
    }
    setPendingModeId(null);
  };
  const handleFamilyPickerClose = () => {
    setFamilyPickerOpen(false);
    setPendingModeId(null);
  };
  const handleChallengeAccept = () => {
    setChallengeModalOpen(false);
    if (onStartMode && pendingChallengeFamilyId) {
      onStartMode('challenge', {
        targetFamilyId: pendingChallengeFamilyId
      });
    }
    setPendingChallengeFamilyId(null);
  };
  const handleChallengeDecline = () => {
    setChallengeModalOpen(false);
    setPendingChallengeFamilyId(null);
  };
  const handleFluencyStart = () => {
    setFluencyIntroOpen(false);
    dataLayer.setFluencyIntroSeen(true);
    if (onStartMode) {
      onStartMode('fluency', {
        targetFamilyId: pendingFluencyConfig?.familyId
      });
    }
    setPendingFluencyConfig(null);
  };
  const handleFluencyCancel = () => {
    setFluencyIntroOpen(false);
    setPendingFluencyConfig(null);
  };
  // ── Derived state ──
  const pendingChallengeMastery = pendingChallengeFamilyId ? state.familyMastery?.[getFamilyMasteryKey(pendingChallengeFamilyId, selectedGroup)]?.masteryScore ?? 0 : 0;
  // ── Fluency cold-start detection ──
  const isFluencyColdStart = useMemo(() => checkFluencyColdStart(state.familyMastery || {}, selectedGroup), [state.familyMastery, selectedGroup]);
  const lastAssessment = assessmentHistory.length > 0 ? assessmentHistory[0] : null;
  // ── FamilyPicker full-screen view ──
  if (familyPickerOpen) {
    return <FamilyPicker onClose={handleFamilyPickerClose} onSelect={handleFamilySelect} mode={familyPickerMode} data-id="element-1808" />;
  }
  // ── Main view ──
  return <PageContainer data-id="element-1809">
      {/* Hero Header */}
      <motion.div variants={itemVariants} data-id="element-1810">
        <PageHero title="Oefenlab" subtitle="Ontdek nieuwe manieren om te oefenen en test je kennis! 🧪" gradientClasses="from-violet-500 via-purple-500 to-fuchsia-500" djayEmotion={labWelcome.emotion} djayVariant={selectedVariant} icon={FlaskConical} iconBgClass="bg-violet-400" floatingEmojis={['🧪', '✨']} data-id="element-1811">
          <HeroStatPill icon={Sparkles} data-id="element-1812">
            {enabledModes.length + 1} manieren om te oefenen
          </HeroStatPill>
          <HeroStatPill icon={ClipboardCheck} data-id="element-1813">
            {assessmentHistory.length} toets
            {assessmentHistory.length !== 1 ? 'en' : ''} gemaakt
          </HeroStatPill>
        </PageHero>
      </motion.div>

      {/* Welcome Banner (B5 — first-time visitors) */}
      <AnimatePresence data-id="element-1814">
        {showWelcome && <motion.div variants={itemVariants} initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -10,
        height: 0,
        marginBottom: 0
      }} transition={{
        type: 'spring',
        stiffness: 400,
        damping: 30
      }} data-id="element-1815">
            <Card className="p-5 border-2 border-violet-200 bg-gradient-to-r from-violet-50 via-purple-50 to-fuchsia-50 rounded-2xl relative mb-6" data-id="element-1816">
              <button onClick={dismissWelcome} className="absolute top-3 right-3 p-1.5 rounded-xl hover:bg-violet-100 text-violet-400 hover:text-violet-600 transition-colors" aria-label="Sluiten" data-id="element-1817">
                <X className="w-4 h-4" data-id="element-1818" />
              </button>
              <div className="flex items-start gap-4" data-id="element-1819">
                <div className="flex-shrink-0" data-id="element-1820">
                  <DjayAvatar size="md" emotion="excited" variant={selectedVariant} animate={true} data-id="element-1821" />
                </div>
                <div data-id="element-1822">
                  <h3 className="text-lg font-black text-slate-800 mb-1" data-id="element-1823">
                    Welkom in het Oefenlab! 🧪
                  </h3>
                  <p className="text-sm text-slate-600 font-medium leading-relaxed" data-id="element-1824">
                    Hier kun je op verschillende manieren oefenen. Kies{' '}
                    <strong data-id="element-1825">Snel & Zeker</strong> om sneller te worden,{' '}
                    <strong data-id="element-1826">Extra oefenen</strong> voor onderwerpen waar je
                    moeite mee hebt, of <strong data-id="element-1827">Uitdaging</strong> als je klaar
                    bent voor iets moeilijkers. Met{' '}
                    <strong data-id="element-1828">Vrij Oefenen</strong> kies je zelf een onderwerp!
                  </p>
                </div>
              </div>
            </Card>
          </motion.div>}
      </AnimatePresence>

      {/* Mission Briefing */}
      <motion.div variants={itemVariants} data-id="element-1829">
        <WorldZone icon={Target} title="Jouw Missie" accentColor="bg-amber-100 text-amber-600" djayMessage="Hier is je missie voor vandaag!" djayVariant={selectedVariant} djayEmotion="pointing" isLast={false} data-id="element-1830">
          <MissionBriefing onModeClick={handleModeClick} data-id="element-1831" />
        </WorldZone>
      </motion.div>

      {/* Lab Stations Grid — includes Vrij Oefenen (B6) */}
      <motion.div variants={itemVariants} data-id="element-1832">
        <WorldZone icon={Beaker} title="Hoe wil je oefenen?" accentColor="bg-fuchsia-100 text-fuchsia-600" djayMessage="Kies een manier om te oefenen! 🧪" djayVariant={selectedVariant} djayEmotion="excited" isLast={false} data-id="element-1833">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1834">
            {enabledModes.map((mode, index) => <motion.div key={mode.id} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2 + index * 0.05
          }} data-id="element-1835">
                <LabStation mode={mode} onClick={() => handleModeClick(mode.id)} data-id="element-1836" />
              </motion.div>)}

            {/* Vrij Oefenen — integrated into the grid (B6) */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2 + enabledModes.length * 0.05
          }} data-id="element-1837">
              <motion.div whileHover={{
              y: -6,
              scale: 1.02
            }} whileTap={{
              scale: 0.96
            }} transition={{
              type: 'spring',
              stiffness: 400,
              damping: 25
            }} className="h-full" data-id="element-1838">
                <div className="h-full flex rounded-[2rem] border-4 transition-all relative overflow-hidden bg-white group cursor-pointer hover:border-transparent hover:shadow-xl border-slate-100" onClick={() => handleModeClick('free')} data-id="element-1839">
                  {/* Left Accent Bar */}
                  <div className="w-4 flex-shrink-0 bg-gradient-to-b from-emerald-400 to-teal-500" data-id="element-1840" />

                  <div className="p-6 flex flex-col h-full relative z-10 flex-1" data-id="element-1841">
                    {/* Top Row: Icon */}
                    <div className="flex items-start justify-between mb-5" data-id="element-1842">
                      <motion.div whileHover={{
                      rotate: [0, -10, 10, -10, 0]
                    }} transition={{
                      duration: 0.5
                    }} className="w-16 h-16 rounded-3xl bg-emerald-100 flex items-center justify-center shadow-inner" data-id="element-1843">
                        <Search className="w-8 h-8 text-emerald-600" strokeWidth={2.5} data-id="element-1844" />
                      </motion.div>
                    </div>

                    {/* Title & Subtitle */}
                    <div className="mb-4 flex-1" data-id="element-1845">
                      <h3 className="text-2xl font-black tracking-tight leading-tight mb-1 text-slate-800" data-id="element-1846">
                        Vrij Oefenen
                      </h3>
                      <p className="text-sm font-bold text-emerald-600" data-id="element-1847">
                        Kies zelf een onderwerp
                      </p>
                      <p className="text-sm text-slate-500 font-medium mt-2 line-clamp-2" data-id="element-1848">
                        Kies zelf een onderwerp en oefen op jouw manier, zonder
                        regels.
                      </p>
                    </div>

                    {/* Action Area */}
                    <div className="mt-auto pt-4 flex items-center justify-between border-t-2 border-slate-50" data-id="element-1849">
                      <div className="flex flex-wrap gap-1.5" data-id="element-1850">
                        <span className="text-xs font-bold px-2.5 py-1 rounded-lg bg-emerald-100 text-emerald-600" data-id="element-1851">
                          Alle vakken
                        </span>
                        <span className="text-xs font-bold px-2.5 py-1 rounded-lg bg-emerald-100 text-emerald-600" data-id="element-1852">
                          Vrije keuze
                        </span>
                      </div>
                      <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center shadow-lg text-white group-hover:scale-110 transition-transform flex-shrink-0" data-id="element-1853">
                        <Play className="w-6 h-6 ml-1" fill="currentColor" data-id="element-1854" />
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </WorldZone>
      </motion.div>

      {/* Oefentoets Section */}
      <motion.div variants={itemVariants} data-id="element-1855">
        <WorldZone icon={ClipboardCheck} title="Oefentoets" accentColor="bg-indigo-100 text-indigo-600" djayMessage="Test je kennis! 📝" djayVariant={selectedVariant} djayEmotion="happy" isLast={true} data-id="element-1856">
          <Card className="p-5 border-2 border-indigo-100 hover:border-indigo-300 bg-gradient-to-r from-indigo-50 via-purple-50 to-fuchsia-50 hover:from-indigo-100 hover:via-purple-100 hover:to-fuchsia-100 transition-all cursor-pointer group rounded-[2rem] overflow-hidden relative" onClick={onStartAssessment} data-id="element-1857">
            <div className="absolute top-0 right-0 w-40 h-40 bg-indigo-200/20 rounded-full blur-2xl -translate-y-1/2 translate-x-1/4 pointer-events-none" data-id="element-1858" />
            <div className="flex items-start gap-4 relative z-10 mb-4" data-id="element-1859">
              <div className="w-14 h-14 rounded-2xl bg-indigo-500 text-white flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform shadow-lg shadow-indigo-200" data-id="element-1860">
                <ClipboardCheck className="w-7 h-7" data-id="element-1861" />
              </div>
              <div className="flex-1" data-id="element-1862">
                <h3 className="text-xl font-black text-slate-800 leading-tight mb-1" data-id="element-1863">
                  Oefentoets
                </h3>
                <p className="text-sm text-indigo-600 font-medium" data-id="element-1864">
                  Test je kennis met vragen uit alle vakken
                </p>
              </div>
            </div>
            <div className="relative z-10 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center" data-id="element-1865">
              {lastAssessment && <div className="text-sm flex items-center justify-between bg-white/60 px-3 py-2 rounded-xl border-2 border-white/40 sm:flex-1" data-id="element-1866">
                  <span className="text-slate-500 font-bold text-xs uppercase tracking-wider" data-id="element-1867">
                    Laatste score:
                  </span>
                  <span className={`font-black ${lastAssessment.score >= 80 ? 'text-emerald-600' : lastAssessment.score >= 60 ? 'text-blue-600' : 'text-amber-600'}`} data-id="element-1868">
                    {lastAssessment.score}%
                  </span>
                </div>}
              <Button onClick={e => {
              e.stopPropagation();
              onStartAssessment?.();
            }} variant="outline" size="md" className="border-2 border-indigo-200 text-indigo-600 hover:bg-indigo-50 rounded-xl font-bold sm:w-auto" data-id="element-1869">
                Start Toets <ArrowRight className="w-4 h-4 ml-2" data-id="element-1870" />
              </Button>
            </div>
          </Card>
        </WorldZone>
      </motion.div>

      {/* ChallengeAcceptModal */}
      {pendingChallengeFamilyId && <ChallengeAcceptModal isOpen={challengeModalOpen} familyId={pendingChallengeFamilyId} masteryScore={pendingChallengeMastery} onAccept={handleChallengeAccept} onDecline={handleChallengeDecline} data-id="element-1871" />}

      {/* FluencyIntro Modal */}
      <FluencyIntro isOpen={fluencyIntroOpen} onStart={handleFluencyStart} onCancel={handleFluencyCancel} isColdStart={isFluencyColdStart} data-id="element-1872" />
    </PageContainer>;
}